import app_config from 'config/app_config'
import apiMiddleware from 'services/api_middleware'

// The one error-message extraction every intents flow shows the user.
export const getApiErrorMessage = (er: any): string =>
	er?.response?.data?.message ?? er?.message ?? 'Something went wrong'

// The 1Click confidentiality level, mirroring the API's own enum: 'basic'/'advanced' settle
// the swap on NEAR's private execution layer. Absent means the backend's default (public).
export type TNearIntentsConfidentiality = 'public' | 'basic' | 'advanced'

export interface INearIntentsToken {
	assetId: string
	decimals: number
	blockchain: string
	symbol: string
	price: number
	priceUpdatedAt: string
	contractAddress?: string
}

// The backend owns swap type and deadline - the client names the route, and may
// optionally override the slippage (basis points).
export interface INearIntentsUserSwapQuoteParams {
	dry: boolean
	originAsset: string
	destinationAsset: string
	amount: string
	refundTo: string
	recipient: string
	slippageTolerance?: number
}

export interface INearIntentsQuoteInfo {
	depositAddress?: string
	amountIn: string
	amountInFormatted: string
	amountInUsd: string
	minAmountIn: string
	amountOut: string
	amountOutFormatted: string
	amountOutUsd: string
	minAmountOut: string
	timeEstimate: number
	deadline?: string
	refundFee?: string
}


// The token list comes through the backend proxy rather than straight from 1Click: the
// backend holds the JWT (unauthenticated 1Click calls carry a 0.2% platform fee) and caches
// the ~186-asset list for five minutes.
export const fetchNearIntentsTokens = async (): Promise<INearIntentsToken[]> => {
	const data = await apiMiddleware({
		url: app_config.services.intentsTokens,
		method: 'POST',
	})

	return data.tokens ?? []
}

// All 1Click interaction goes through the backend's single intents API set - the client
// bundle never talks to 1Click directly. The quote endpoint prices every purpose, keyed by
// an explicit type. A payment names its invoices - one or several, one deposit settles them
// all and the backend marks each linked invoice paid.
export const requestNearIntentsPaymentQuote = async (params: {
	ids: string[]
	originAsset: string
	refundTo: string
	dry?: boolean
	slippageTolerance?: number
	confidentiality?: TNearIntentsConfidentiality
}): Promise<{ quote: INearIntentsQuoteInfo }> =>
	apiMiddleware({
		url: app_config.services.intentsQuote,
		method: 'POST',
		data: { type: 'PAYMENT', ...params },
	})

export const requestNearIntentsUserSwapQuote = async (
	params: INearIntentsUserSwapQuoteParams
): Promise<{ quote: INearIntentsQuoteInfo }> =>
	apiMiddleware({
		url: app_config.services.intentsQuote,
		method: 'POST',
		data: { type: 'USER_SWAP', ...params },
	})

// A withdrawal: the treasury pays the contractor out. A dry quote previews what arrives; a
// committed quote is authorized by an EIP-712 signature (see signWithdrawal) and moves
// treasury funds, so the backend requires deadline + signature together.
export const requestNearIntentsWithdrawalQuote = async (params: {
	id: string
	destinationAsset: string
	recipient: string
	dry?: boolean
	slippageTolerance?: number
	confidentiality?: TNearIntentsConfidentiality
	deadline?: number
	signature?: string
	// The signing scheme and its NEAR-only proof fields; the backend routes verification by it.
	signatureType?: 'EVM' | 'NEAR' | 'TRON'
	publicKey?: string
	nonce?: string
}): Promise<{ quote: INearIntentsQuoteInfo }> =>
	apiMiddleware({
		url: app_config.services.intentsQuote,
		method: 'POST',
		data: { type: 'WITHDRAWAL', ...params },
	})

// Invoice cancellation: the sender refunds the treasury-held funds to themselves. Same request
// shape as a withdrawal, keyed by REFUND so the backend authorizes it against the sender.
export const requestNearIntentsRefundQuote = async (params: {
	id: string
	destinationAsset: string
	recipient: string
	dry?: boolean
	slippageTolerance?: number
	confidentiality?: TNearIntentsConfidentiality
	deadline?: number
	signature?: string
}): Promise<{ quote: INearIntentsQuoteInfo }> =>
	apiMiddleware({
		url: app_config.services.intentsQuote,
		method: 'POST',
		data: { type: 'REFUND', ...params },
	})

// One entry per invoice an intent touches, keyed by invoice id: the invoice's status and
// its quote-time share of the deposit.
export type INearIntentsInvoiceStatuses = Record<string, { status: string; amount: string }>

// Status and deposit are purpose-agnostic: the deposit address names the intent, the
// backend scopes it to the requesting user. `invoices` is null for a user swap.
export const fetchNearIntentsStatus = async (
	depositAddress: string
): Promise<{
	intent?: { status: string }
	invoices: INearIntentsInvoiceStatuses | null
}> =>
	apiMiddleware({
		url: app_config.services.intentsStatus,
		method: 'POST',
		data: { depositAddress },
	})

export const submitNearIntentsDepositTxHash = async (
	depositAddress: string,
	txHash: string
): Promise<void> =>
	apiMiddleware({
		url: app_config.services.intentsDeposit,
		method: 'POST',
		data: { depositAddress, txHash },
	})
